"use client";

import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { ROUTER_PATH } from "@/src/config";
import type { OnboardingWizardStep } from "@/src/components/OnboardingWizard/types";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { useShellRouter } from "@/src/hooks/useShellAwareRouter";
import { rootStore } from "@/src/store";
import { useChatOnboardingApi } from "./useChatOnboardingApi";
import {
  clearQuizResultContext,
  getQuizResultContext,
} from "./quizResultContextStorage";
import { trackObRestart } from "./onboardingAnalytics";
import {
  clearWizardSession,
  getWizardFurthestStep,
  shouldResetWizardForInterrupt,
} from "./onboardingWizardSessionStorage";
import {
  isOwnerProfileReadyForEntryGate,
  ownerNeedsWelcomeNickName,
} from "./ownerWelcomeGate";
import { useOnboardingHarnessQuery } from "./useOnboardingHarnessQuery";

export interface OnboardingWizardBootstrapState {
  ready: boolean;
  initialStep: OnboardingWizardStep;
  /** Remount key after paywall-before interrupt recovery */
  resetKey: number;
}

const INITIAL_STATE: OnboardingWizardBootstrapState = {
  ready: false,
  initialStep: "score-intro",
  resetKey: 0,
};

function resolveWizardReadyState(): Omit<
  OnboardingWizardBootstrapState,
  "ready"
> & { ready: true } {
  const needsInterruptReset =
    Boolean(getQuizResultContext()) || shouldResetWizardForInterrupt();

  if (needsInterruptReset) {
    const lastStep = getWizardFurthestStep();
    if (lastStep) {
      trackObRestart(lastStep);
    }
    clearQuizResultContext("wizard-interrupt-recovery");
    clearWizardSession();
    return {
      ready: true,
      initialStep: "score-intro",
      resetKey: 1,
    };
  }

  const furthest = getWizardFurthestStep();

  return {
    ready: true,
    initialStep: furthest ?? "score-intro",
    resetKey: 0,
  };
}

/**
 * Wizard mount bootstrap: completed guard + paywall-before interrupt → UI3.
 * Skips `onboarding-status` when `nick_name` is missing (backend may error).
 */
export function useOnboardingWizardBootstrap(): OnboardingWizardBootstrapState {
  const router = useShellRouter();
  const harness = useOnboardingHarnessQuery();
  const onboardingApi = useChatOnboardingApi();
  const { chatApi } = useOspApiClientProvider();
  const { isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const { ownerInfo, profileState } = useSnapshot(rootStore.user);
  const [state, setState] =
    useState<OnboardingWizardBootstrapState>(INITIAL_STATE);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (harness.enabled && harness.auth === "onboarding") {
      if (bootstrappedRef.current) {
        return;
      }
      bootstrappedRef.current = true;
      setState({
        ready: true,
        initialStep: harness.wizardStep ?? "score-intro",
        resetKey: 0,
      });
      return;
    }

    if (harness.enabled && harness.auth === "completed") {
      if (bootstrappedRef.current) {
        return;
      }
      bootstrappedRef.current = true;
      router.replace(ROUTER_PATH.BRIEF);
      return;
    }

    if (isFetching || !isLoggedIn) {
      bootstrappedRef.current = false;
      return;
    }
    if (!isOwnerProfileReadyForEntryGate(profileState)) {
      return;
    }
    if (bootstrappedRef.current) {
      return;
    }

    const needsOnboardingStatus = !ownerNeedsWelcomeNickName(ownerInfo);
    if (needsOnboardingStatus && !chatApi) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (ownerNeedsWelcomeNickName(ownerInfo)) {
        if (cancelled) {
          return;
        }
        bootstrappedRef.current = true;
        setState(resolveWizardReadyState());
        return;
      }

      const status = await onboardingApi.getOnboardingStatus();
      if (cancelled) {
        return;
      }

      bootstrappedRef.current = true;

      if (!status.shouldEnter) {
        router.replace(ROUTER_PATH.BRIEF);
        return;
      }

      setState(resolveWizardReadyState());
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    harness.auth,
    harness.enabled,
    harness.wizardStep,
    isFetching,
    isLoggedIn,
    chatApi,
    onboardingApi,
    ownerInfo?.nick_name,
    profileState,
    router,
  ]);

  return state;
}
